import React, { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { Box, Flex, Text } from '@podiumhq/design-system';

import { useCreditCardContext } from 'contexts/CreditCardDetailsContextProvider';
import { useSelectPlanCheckoutContext } from 'contexts/SelectPlanCheckoutContextProvider';
import useTrackAmplitudeEvent from 'hooks/useTrackAmplitudeEvent';

import CreditCardFields from '../CreditCardDetails/CreditCardFields';
import CreditCardIconsRow from '../CreditCardDetails/CreditCardIconsRow';

import { ErrorNotification } from './components/ErrorNotification';
import { BillingAddressOptionsForm } from './components/FormItems';
import {
  SelectPlanCheckoutContentBox,
  SelectPlanCheckoutContentFlexWrapper,
  SelectPlanCheckoutDescription,
  SelectPlanCheckoutTitle,
} from './components/layout';
import SelectedPlanSummary from './components/SelectedPlanSummary';
import { SUMMARY_VIEW_ROUTE } from './constants';
import { useSetUpPurchaseIntent } from './hooks';
import {
  paymentDetailsTitleStyles,
  SelectPlanCheckoutButton,
  SelectPlanCheckoutPaymentFieldsWrapper,
} from './styles';
import SelectPlanTermForm from './components/FormItems/SelectPlanTermForm';

const PaymentView = () => {
  const { isMobile, selectedTerm, setSelectedTerm } =
    useSelectPlanCheckoutContext() || {};
  useTrackAmplitudeEvent('selectPlanCheckout.paymentInfo.visited');

  const [stripeError, setStripeError] = useState('');

  return (
    <>
      <SelectPlanCheckoutTitle>How do you want to pay?</SelectPlanCheckoutTitle>
      <SelectPlanCheckoutDescription mb="40px">
        Add your payment info and billing address.
      </SelectPlanCheckoutDescription>

      <SelectPlanCheckoutContentFlexWrapper>
        {isMobile && <SelectedPlanSummary mb="21px" maxWidth="100%" />}

        <SelectPlanCheckoutContentBox>
          <Box mb="32px">
            <Text {...paymentDetailsTitleStyles}>Plan Type</Text>
            <SelectPlanTermForm
              onChange={setSelectedTerm}
              selectedTermValue={selectedTerm}
            />
          </Box>

          <SelectPlanCheckoutPaymentFieldsWrapper>
            <Flex align="flex-start" justify="space-between">
              <Text {...paymentDetailsTitleStyles}>Card Information</Text>
              <CreditCardIconsRow mr="10px" />
            </Flex>

            <ErrorNotification errorMessage={stripeError} shouldScrollToError />

            <CreditCardFields
              fullWidthForm={isMobile}
              trackOnBlurPath="selectPlanCheckout.paymentInfo"
            />
          </SelectPlanCheckoutPaymentFieldsWrapper>

          <BillingAddressOptionsForm />

          <PaymentContinueButton setStripeError={setStripeError} />
        </SelectPlanCheckoutContentBox>

        {!isMobile && <SelectedPlanSummary mb="0" maxWidth="360px" />}
      </SelectPlanCheckoutContentFlexWrapper>
    </>
  );
};

const PaymentContinueButton = ({ setStripeError }) => {
  const { validateCreditCardFields } = useCreditCardContext() || {};
  const { changeCheckoutPage, hasValidBillingAddress } =
    useSelectPlanCheckoutContext() || {};
  const trackEvent = useTrackAmplitudeEvent();
  const { error, handleSetUpIntentData, stripeError } =
    useSetUpPurchaseIntent(trackEvent);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleContinueClick = () => {
    setIsSubmitting(true);
    trackEvent('selectPlanCheckout.paymentInfo.continueClicked');

    const validCC = validateCreditCardFields();
    const validBilling = hasValidBillingAddress({ trackEvent });

    if (!validCC || !validBilling) {
      return setIsSubmitting(false);
    }

    handleSetUpIntentData()
      .then((isSuccessful) => {
        setIsSubmitting(false);

        if (isSuccessful) {
          changeCheckoutPage(SUMMARY_VIEW_ROUTE);
        }
      })
      .catch((error) => {
        setIsSubmitting(false);

        Sentry.captureException(error, {
          extra: { message: 'CheckoutSubmitPaymentInfoError' },
        });
      });
  };

  useEffect(() => {
    setStripeError && setStripeError(stripeError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeError]);

  return (
    <>
      <ErrorNotification errorMessage={error} mt="24px" shouldScrollToError />

      <SelectPlanCheckoutButton
        disabled={isSubmitting}
        fillColor="blue.base"
        id="payment-view-continue-button"
        isLoading={isSubmitting}
        margin="32px 0 20px 0"
        onClick={handleContinueClick}
        width="100%"
      >
        Continue to checkout
      </SelectPlanCheckoutButton>
    </>
  );
};

export default PaymentView;
